
import React, { useEffect, useState } from 'react';
import { Box, TableBody } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  MainContainer,
  HeaderContainer,
  PageTitle,
  CreateButton,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  ActionButton,
  DeleteIconWrapper
} from './styles';
import CustomPagination from '../../../components/CustomPagination';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { useSidebar } from '../../../context/SidebarContext';
import FacilityTypeModal from './components/FacilityTypeModal';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../store';
import { searchFacilityTypes, deleteFacilityType } from '../../../modules/masterdata/actions';
import {
  selectFacilityTypesList,
  selectSearchFacilityTypesCount,
  selectCreateFacilityTypeSuccess,
  selectCreateFacilityTypeError,
  selectUpdateFacilityTypeSuccess,
  selectUpdateFacilityTypeError,
  selectDeleteFacilityTypeSuccess,
  selectDeleteFacilityTypeError
} from '../../../modules/masterdata/selectors';
import {
  resetCreateFacilityTypeState,
  resetUpdateFacilityTypeState,
  resetDeleteFacilityTypeState
} from '../../../modules/masterdata/reducers';
import { IFacilityType } from '../../../modules/masterdata/types';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import AlertPopup from '../../../components/common/AlertPopup';

const FacilityType: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  const dispatch = useDispatch<AppDispatch>();

  const facilityTypes = useSelector(selectFacilityTypesList);
  const totalCount = useSelector(selectSearchFacilityTypesCount);

  // Success/Error Selectors
  const createSuccess = useSelector(selectCreateFacilityTypeSuccess);
  const createError = useSelector(selectCreateFacilityTypeError);
  const updateSuccess = useSelector(selectUpdateFacilityTypeSuccess);
  const updateError = useSelector(selectUpdateFacilityTypeError);
  const deleteSuccess = useSelector(selectDeleteFacilityTypeSuccess);
  const deleteError = useSelector(selectDeleteFacilityTypeError);

  const PAGE_SIZE = 10;

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<IFacilityType | null>(null);

  // Confirmation Dialog state
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // Alert state
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setActivePage('facility-type');
    setPath([
      { title: 'Settings', path: '/master-data' },
      { title: 'Master Data', path: '/master-data' },
      { title: 'Facility Type', path: '/master-data/facility-types' },
    ]);
    setIsShow(true);
    dispatch(searchFacilityTypes({ search: '', page: 1, page_size: PAGE_SIZE }));
  }, [setActivePage, setPath, setIsShow, dispatch]);

  const [page, setPage] = useState(1);

  // Handle Notifications and Resets
  useEffect(() => {
    if (createSuccess) {
      setAlert({ message: 'Facility Type created successfully', severity: 'success' });
      dispatch(searchFacilityTypes({ search: '', page, page_size: PAGE_SIZE }));
      dispatch(resetCreateFacilityTypeState());
    }
    if (createError) {
      setAlert({ message: createError, severity: 'error' });
      dispatch(resetCreateFacilityTypeState());
    }

    if (updateSuccess) {
      setAlert({ message: 'Facility Type updated successfully', severity: 'success' });
      dispatch(searchFacilityTypes({ search: '', page, page_size: PAGE_SIZE }));
      dispatch(resetUpdateFacilityTypeState());
    }
    if (updateError) {
      setAlert({ message: updateError, severity: 'error' });
      dispatch(resetUpdateFacilityTypeState());
    }

    if (deleteSuccess) {
      setAlert({ message: 'Facility Type deleted successfully', severity: 'success' });
      dispatch(searchFacilityTypes({ search: '', page, page_size: PAGE_SIZE }));
      dispatch(resetDeleteFacilityTypeState());
    }
    if (deleteError) {
      setAlert({ message: deleteError, severity: 'error' });
      dispatch(resetDeleteFacilityTypeState());
    }
  }, [createSuccess, createError, updateSuccess, updateError, deleteSuccess, deleteError, dispatch, page]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    dispatch(searchFacilityTypes({ search: '', page: newPage, page_size: PAGE_SIZE }));
  };

  const handleCreateClick = () => {
    setSelectedType(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (type: IFacilityType) => {
    setSelectedType(type);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
    setIsConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      await dispatch(deleteFacilityType(deleteId));
      setIsConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const totalPages = Math.ceil((totalCount || 0) / PAGE_SIZE);

  return (
    <Box>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      <MainContainer>
        <HeaderContainer>
          <PageTitle variant="h6">Manage Facility Types</PageTitle>
          <CreateButton variant="contained" disableElevation onClick={handleCreateClick}>
            Create Facility Type
          </CreateButton>
        </HeaderContainer>

        <StyledTableContainer>
          <StyledTable>
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell>Id</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {facilityTypes && facilityTypes.length > 0 ? (
                facilityTypes.map((type: IFacilityType, index: number) => (
                  <StyledTableRow key={type.facility_type_id}>
                    <StyledTableCell>{type.facility_type_id}</StyledTableCell>
                    <StyledTableCell>{type.facility_type_name}</StyledTableCell>
                    <StyledTableCell>{type.facility_type_description}</StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <ActionButton onClick={() => handleEditClick(type)}>
                          <EditIcon fontSize="small" />
                        </ActionButton>
                        <DeleteIconWrapper onClick={() => handleDeleteClick(type.facility_type_id)}>
                          <DeleteIcon fontSize="small" />
                        </DeleteIconWrapper>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={4} align="center">No facility types found.</StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <CustomPagination
            totalPages={totalPages > 0 ? totalPages : 1}
            currentPage={page}
            onPageChange={handlePageChange}
          />
        </Box>
      </MainContainer>

      <FacilityTypeModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        type={selectedType}
      />

      <ConfirmationDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Facility Type"
        message="Are you sure you want to delete this facility type?"
      />
    </Box>
  );
};

export default FacilityType;
